import { Fragment } from 'react';
import Skeleton from '../../reusable/Skeleton/Skeleton';
import { t } from '../../i18n/i18n';
import { platforms } from './data';

/**
 * The Select Plan card while `GET /v3/challenges/` is in flight.
 *
 * It reuses the real layout classes — `ta-section`, `ta-plans`/`ta-plan`,
 * `ta-types`/`ta-type`, `ta-sizes`/`ta-size`, `ta-platforms`/`ta-platform` —
 * rather than approximating them, so the tiles are already the right height and
 * the right grid when the data lands and the real rows take their place. Only
 * the *contents* of a tile are stand-ins.
 *
 * The counts are what the design draws: three steps, three account types, six
 * sizes. Platforms are a local list, not an API one, so that row skeletons the
 * exact number of tiles it is about to render.
 */


/** Title + description, the same two lines every section head carries. */
const Head = () => (
  <div className="ta-section__head">
    <Skeleton radius={4} w="190px" h={20} />
    <Skeleton radius={4} w="min(420px, 80%)" h={13} />
  </div>
);

const range = (n) => Array.from({ length: n }, (_, i) => i);

export default function CheckoutSkeleton() {
  const rows = [
    // Choose your Step — icon tile, then a label over a note.
    <div className="ta-plans" style={{ '--ta-cols': 3 }}>
      {range(3).map((i) => (
        <span key={i} className="ta-plan ta-plan--skel">
          <span className="ta-plan__tile" />
          <span className="ta-plan__text">
            <Skeleton radius={4} w="110px" h={13} />
            <Skeleton radius={4} w="72px" h={10} />
          </span>
        </span>
      ))}
    </div>,

    // Account Type — the radio, then a single label.
    <div className="ta-types" style={{ '--ta-cols': 3 }}>
      {range(3).map((i) => (
        <span key={i} className="ta-type ta-type--skel">
          <span className="ta-radio" />
          <Skeleton radius={4} w="120px" h={13} />
        </span>
      ))}
    </div>,

    // Account Size — radio and balance, the hairline, then fee over note.
    <div className="ta-sizes" style={{ '--ta-cols': 6 }}>
      {range(6).map((i) => (
        <span key={i} className="ta-size ta-size--skel">
          <span className="ta-size__head">
            <span className="ta-radio" />
            <Skeleton radius={4} w="64px" h={13} />
          </span>
          <span className="ta-size__rule" />
          <Skeleton radius={4} w="56px" h={12} />
          <Skeleton radius={4} w="72px" h={11} />
        </span>
      ))}
    </div>,

    // Platform — one centred block where each wordmark will be.
    <div className="ta-platforms">
      {platforms.map((option) => (
        <span key={option.id} className="ta-platform ta-platform--skel">
          <span className="ta-platform__inner">
            <Skeleton radius={4} w={`${option.logoWidth}px`} h={16} />
          </span>
        </span>
      ))}
    </div>,
  ];

  return (
    <div className="ta-sections" aria-busy="true" aria-label={t('Loading plans')}>
      {rows.map((row, i) => (
        // The rule between sections, exactly as the real card spaces them.
        <Fragment key={i}>
          {i > 0 && <span className="ta-rule" />}
          <section className="ta-section">
            <Head />
            {row}
          </section>
        </Fragment>
      ))}
    </div>
  );
}
